import React, { useState } from "react";
import {
  Question, CaretDown, CaretUp, CurrencyDollar, Lightning, VideoCamera,
  Image, Microphone, FilmStrip, Robot, Key, Coins, Warning, ArrowRight,
  Info, Sparkle, FilmReel, LinkedinLogo, MagnifyingGlass,
} from "@phosphor-icons/react";

const SECTIONS = [
  {
    id: "costs",
    title: "Costs & Pricing",
    icon: CurrencyDollar,
    color: "#10b981",
    items: [
      {
        q: "How much does it cost to create one video?",
        a: `Each Auto Create video uses three external AI services (you provide the keys — BYOK):

• Magic Hour (AI Talking Photo for face-preserved lip-synced video)
• Fish Audio (voice clone TTS)
• muapi.ai (image/audio hosting + optional B-roll + captions)

Credit math per video length:

  15-sec reel:  ~720 Magic Hour credits + ~$0.02 Fish Audio + ~$0.05 muapi = ~$1.00
  30-sec reel:  ~1,440 MH credits + ~$0.04 Fish + ~$0.10 muapi = ~$2.00
  60-sec reel:  ~2,880 MH credits + ~$0.08 Fish + ~$0.15 muapi = ~$4.00

Magic Hour is the biggest cost. On their Creator plan ($12/mo = 10,000 credits) you get roughly 3-4 × 60-sec videos per month, OR ~13 × 15-sec clips.

Optional add-ons (all via muapi):
  B-Roll clips: +$0.15 each (typically 1-2 per video)
  AI Captions: +$0.10-$0.20

Keep a $10-$20 buffer in your muapi account.`,
      },
      {
        q: "How much should I budget monthly?",
        a: `Plan by volume and length:

Magic Hour (the biggest spend):
  ~5 × 60-sec videos/month: Creator plan = $12/mo (10,000 credits)
  ~20 × 30-sec videos/month: Pro plan = $49/mo (40,000 credits)
  50+ videos/month: Business plan = $149/mo (unlimited)

Fish Audio (voice): ~$3-$10/month for most creators.
muapi.ai (hosting + b-roll + captions): $5-$15/month.

Total realistic monthly spend for an active creator: $25-$70/month across all three services, depending on video length + add-ons.`,
      },
      {
        q: "What does a Lifestyle Video cost?",
        a: `Lifestyle videos (AI Twin image → short animated clip) use muapi.ai:

  Standard Lifestyle Video: ~$0.75 per clip
  With outfit change (Nano Banana): +$0.10-$0.15

These are standalone visual clips — no voice or lip-sync.`,
      },
      {
        q: "How much does AI Twin image generation cost?",
        a: `AI Twin images use Nano Banana or Flux Kontext Pro via muapi.ai:

  Nano Banana 2 (text-to-image): ~$0.03-$0.05
  Nano Banana Pro: ~$0.05-$0.08
  Flux Kontext Pro (reference/outfit): ~$0.05-$0.10
  Nano Banana Edit (outfit swap): ~$0.05-$0.08

Very affordable — you can generate dozens of images for under $1.`,
      },
      {
        q: "What about text generation (scripts, hooks, LinkedIn posts)?",
        a: `All text generation uses Claude Sonnet 4.5 via the Emergent LLM Key. This is included in your platform credits — no additional API cost from your BYOK keys.

  Script generation: 1 credit
  Hook + Caption: 1 credit
  LinkedIn post: 1 credit
  AI Tea Tuesday script: 1 credit
  Series Studio storyboard: 2 credits`,
      },
      {
        q: "How does the credit system work?",
        a: `Credits are the platform's internal currency for AI text generation:

  New accounts start with 50 free credits
  Credits are used for: scripts, hooks, LinkedIn posts, series storyboards
  Credits are NOT used for: video generation, voice cloning, image generation (those use your BYOK API keys directly)

You can buy more credits in Account Settings, or subscribe for monthly allowances:
  Creator ($19.99/mo): 200 credits/month
  Pro ($39.99/mo): 500 credits/month  
  Business ($79.99/mo): Unlimited credits`,
      },
    ],
  },
  {
    id: "byok",
    title: "API Keys (BYOK)",
    icon: Key,
    color: "#8b5cf6",
    items: [
      {
        q: "What API keys do I need?",
        a: `To use all features, you need THREE API keys (all BYOK):

1. Magic Hour — REQUIRED for Auto Create video (AI Talking Photo with face-preserved lip-sync)
   Get it at: https://magichour.ai (sign up → Developer Hub → API Keys, copy key starting with mhk_live_)
   Recommended plan: Creator ($12/mo = 10,000 credits)

2. muapi.ai — REQUIRED for AI Twin image generation, Scene Studio, outfit swap, B-roll, captions, file hosting
   Get it at: https://muapi.ai (sign up → Dashboard → API Keys)
   Usage-based, top up $10-20 to start

3. Fish Audio — REQUIRED for voice cloning in Auto Create
   Get it at: https://fish.audio (sign up → Settings → API Keys)
   Plus your Voice Model ID from Voice Studio

Add all three keys in Account Settings → Credits & Keys → API Keys (BYOK).`,
      },
      {
        q: "How do I get my Magic Hour API key?",
        a: `1. Go to https://magichour.ai and sign up (free tier available, but Creator plan at $12/mo is recommended for serious creators)
2. Click your profile icon → Developer Hub → API Keys
3. Click "Create new key", give it a name (e.g., "The Branding Bar"), and copy the key (starts with mhk_live_)
4. In The Branding Bar, go to Account Settings → Credits & Keys → API Keys (BYOK)
5. Paste your key in the Magic Hour card and click Save

If you hit a "402 Magic Hour credits needed" error in Auto Create, top up at magichour.ai/subscription. The error message tells you exactly how many credits you need vs have.`,
      },
      {
        q: "How do I set up my voice clone on Fish Audio?",
        a: `1. Go to https://fish.audio
2. Sign up and navigate to "Voice Studio"
3. Upload a clear audio sample of your voice (30-60 seconds, minimal background noise)
4. Fish Audio will create a voice model — copy the Model ID
5. In The Branding Bar, go to Account Settings > API Keys
6. Paste your Fish Audio API key and Voice ID

Tip: The voice ID is the alphanumeric code in the URL when viewing your model (e.g., 10f74f05d6d14ca7a4c1be94abd97491). If you paste the full URL, we'll extract the ID automatically.`,
      },
      {
        q: "How do I top up my muapi.ai balance?",
        a: `1. Go to https://muapi.ai and log in
2. Click on your profile > "Billing" or "Top Up"
3. Add funds via credit card or crypto
4. Your balance is shared across all muapi services (Seedance, Nano Banana, lip-sync, etc.)

Check your current balance anytime at muapi.ai/dashboard.`,
      },
      {
        q: "Can I use the platform without my own API keys?",
        a: `Text-based features (scripts, hooks, LinkedIn posts, series storyboards) work with platform credits only — no BYOK keys needed.

For video and image generation, you'll need:
  • Magic Hour key — Required for Auto Create video
  • muapi.ai key — Required for AI Twin images, Scene Studio, B-roll, captions
  • Fish Audio key — Required for voice cloning in Auto Create

Without these keys, you can still use: Script Studio, Hook + Caption, LinkedIn Engine, AI Tea Tuesday, Brand Knowledge Base, Content Vault, and Series Studio (storyboarding only).`,
      },
    ],
  },
  {
    id: "features",
    title: "Features & How-To",
    icon: Sparkle,
    color: "#E91E8C",
    items: [
      {
        q: "What is Auto Create?",
        a: `Auto Create is the main video pipeline. It takes a topic and automatically:

1. Writes a script (AI-powered, uses your brand voice — Claude Sonnet 4.5)
2. Clones your voice (Fish Audio)
3. Generates a face-preserved lip-synced talking video of your AI twin (Magic Hour AI Talking Photo)
4. Rehosts the final video on our CDN for a permanent URL
5. Adds B-Roll clips (optional — via muapi.ai)
6. Adds captions (optional — via muapi.ai)

Renders take ~5 min for 15-sec videos, ~14 min for 30-sec, ~28 min for 60-sec. You can close the tab — a bell notification fires the moment your video is ready.

The result is a ready-to-post short-form video for TikTok, Instagram Reels, YouTube Shorts, or LinkedIn.`,
      },
      {
        q: "Why does video generation take so long?",
        a: `Magic Hour's AI Talking Photo model renders at roughly 2-3 minutes per 5 seconds of output. This is a hard limit of their generative model — the render time doesn't change based on your plan tier. The "priority queue" on higher plans only helps during peak load.

Why is it worth the wait? Because Magic Hour Talking Photo preserves your face EXACTLY while syncing lips perfectly — where competing models (Seedance, Kling, SyncLabs) produced facial distortion and poor lip-sync.

To keep the experience painless:
  • Live progress banner on Auto Create page with ETA countdown
  • In-app bell notification when video completes
  • Toast popup with "View" button
  • You can close the tab and come back — nothing is lost`,
      },
      {
        q: "What is Bulk Create?",
        a: `Bulk Create lets you queue up to 15 Auto Create videos + 10 Lifestyle videos at once. The AI:

  Auto-generates unique topics based on your brand
  Rotates through your AI Twin images
  Processes everything in parallel

Great for batch content creation — set it up and walk away. All videos appear in your Content Vault when done.`,
      },
      {
        q: "What is the AI Twin Gallery?",
        a: `Your AI Twin Gallery stores all your reference images. Three tabs:

  • Gallery — all your saved AI Twin portraits
  • Create — generate new AI Twin images from a text prompt (Nano Banana / Flux)
  • Scenes — NEW! Pick from 62 preset scene prompts to put your existing AI Twin in new settings

These images are used as the "face" in your Auto Create and Lifestyle videos.`,
      },
      {
        q: "What is Scene Studio (the \"Scenes\" tab)?",
        a: `Scene Studio lets you take ANY existing AI Twin image and drop them into a new setting without writing complex prompts yourself. It uses Flux Kontext Pro to regenerate the background/setting while keeping your face exactly consistent.

Flow:
1. Pick a source AI Twin image
2. Pick a category (12 total: Office & Business, Home & Lifestyle, Close-up & Vlog, Car & Travel, Outdoor & City, Beauty & Fashion, Fitness & Wellness, Cafe & Restaurant, Stage & Event, Luxury & Travel, Studio & Podcast, Retail & Storefront)
3. Click any of the 62 preset cards — the prompt auto-fills
4. Edit the prompt if you want, or write your own from scratch
5. Toggle "Add realism boost" (ON by default) to append pro-photography modifiers (natural skin texture, film grain, vignetting, 4K)
6. Click Generate

The category order adapts to your brand — Beauty brands see Beauty+Lifestyle first, Business brands see Office+Studio first.

Cost: ~$0.05-$0.10 per scene (via muapi.ai).`,
      },
      {
        q: "What is Series Studio?",
        a: `Series Studio helps you plan multi-episode content series. The AI:

1. Generates a storyboard with episode titles, hooks, scenes, and CTAs
2. You can customize the genre, vibe, and target audience
3. Each episode can be sent to Auto Create for video generation

Perfect for building a consistent content narrative across multiple videos.`,
      },
      {
        q: "What is the Brand Knowledge Base?",
        a: `The Brand Knowledge Base stores everything about your brand — goals, offers, CTAs, target audience, and key topics.

Everything you add here is automatically used by ALL AI content generation across the platform. The more context you provide, the more personalized and on-brand your content will be.

You can upload documents (PDF, DOCX, TXT) or add text entries directly.`,
      },
      {
        q: "What is the Content Vault?",
        a: `The Content Vault is your content library. Every completed video, script, and caption is saved here.

You can:
  Preview all your content
  Download videos and scripts
  Export to CSV for bulk scheduling
  Save from any section using the "Save to Vault" button`,
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    icon: Warning,
    color: "#f59e0b",
    items: [
      {
        q: "\"Magic Hour credits needed\" error on video generation",
        a: `This means your Magic Hour account doesn't have enough credits for the requested video length. The error message shows you exactly how many credits are needed vs how many you have.

Credit cost per length:
  15-sec video: ~720 credits
  30-sec video: ~1,440 credits
  60-sec video: ~2,880 credits

Fix: Go to https://magichour.ai/subscription and either top up or upgrade your plan. The Creator plan at $12/mo gives 10,000 credits, which is enough for 3-4 × 60-sec videos per month. Power users usually want the Pro plan ($49/mo = 40,000 credits).`,
      },
      {
        q: "\"Video generation timed out\" on Auto Create",
        a: `Magic Hour Talking Photo renders at roughly 2-3 minutes per 5 seconds of video output. A 60-sec video can take 25-30 minutes. Our background poll waits up to 45 minutes before timing out.

If you do hit a timeout, don't panic:
1. The video is probably still rendering on Magic Hour's side
2. When you refresh the Auto Create page, we automatically re-check Magic Hour and finalize the job
3. You'll also get a bell notification the moment it completes

Tip: You can close the tab during rendering. The notification system will alert you via the top-right bell when your video is ready.`,
      },
      {
        q: "\"Insufficient Balance\" error on voice generation",
        a: `This means your Fish Audio account ran out of credits.

Fix: Go to https://fish.audio → Settings → Billing → Add funds.

Fish Audio voice cloning costs approximately $0.01-$0.03 per generation. A $5 top-up will last hundreds of voice generations.`,
      },
      {
        q: "\"muapi upload failed\" or captions/B-roll errors",
        a: `This means your muapi.ai account needs a top-up or the service is temporarily overloaded.

Fix: Check your balance at https://muapi.ai/dashboard. A $10-20 top-up covers hundreds of operations (hosting + B-roll + captions).`,
      },
      {
        q: "\"Image file not found\" error",
        a: `This can happen after a platform update if your AI Twin images weren't backed up.

Fix: Re-upload your AI Twin images in the AI Twin Gallery. All newly uploaded images are now stored persistently and survive platform updates.`,
      },
      {
        q: "Google login not working on mobile",
        a: `If Google login sends you back to the home page:

1. Make sure you're using a modern browser (Chrome, Safari)
2. Clear your browser cache and cookies for this site
3. Try the email/password login as an alternative
4. If the issue persists, try signing up with email first, then link Google later`,
      },
      {
        q: "Can't see my content after logging in",
        a: `Each account's content is private and isolated. Make sure you're logged into the same account you used to create the content.

If you signed up with Google and later try email login (or vice versa), you may have two separate accounts. Contact support to merge them.`,
      },
    ],
  },
];

const FAQItem = ({ item, isOpen, toggle }) => (
  <div className="border-b border-zinc-100 last:border-0">
    <button
      onClick={toggle}
      className="w-full flex items-start justify-between gap-3 py-4 text-left hover:bg-zinc-50/50 px-1 transition-all"
      data-testid={`faq-toggle-${item.q.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
    >
      <span className={`text-sm font-medium ${isOpen ? "text-[#E91E8C]" : "text-zinc-800"}`}>{item.q}</span>
      {isOpen ? <CaretUp size={16} className="text-[#E91E8C] shrink-0 mt-0.5" /> : <CaretDown size={16} className="text-zinc-400 shrink-0 mt-0.5" />}
    </button>
    {isOpen && (
      <div className="pb-4 px-1">
        <pre className="text-sm text-zinc-600 whitespace-pre-wrap font-sans leading-relaxed">{item.a}</pre>
      </div>
    )}
  </div>
);

const HelpPricing = ({ isPublic = false }) => {
  const [openItems, setOpenItems] = useState({});
  const [activeSection, setActiveSection] = useState("costs");
  const [search, setSearch] = useState("");

  const toggle = (key) => setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));

  const filteredSections = search
    ? SECTIONS.map((s) => ({
        ...s,
        items: s.items.filter(
          (i) => i.q.toLowerCase().includes(search.toLowerCase()) || i.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((s) => s.items.length > 0)
    : SECTIONS.filter((s) => s.id === activeSection);

  return (
    <div className={`${isPublic ? "max-w-4xl mx-auto px-4 py-12" : "space-y-5"}`} data-testid="help-pricing-page">
      <div>
        <h2 className={`font-bold text-zinc-900 ${isPublic ? "text-3xl sm:text-4xl text-center" : "text-2xl"}`}>
          {isPublic ? "Help & Pricing" : "Help & Pricing"}
        </h2>
        <p className={`text-zinc-500 mt-1 ${isPublic ? "text-center text-base" : "text-sm"}`}>
          Cost estimates, feature guides, API setup, and troubleshooting
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-md mx-auto sm:mx-0">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help articles..."
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/20 focus:border-[#E91E8C]"
          data-testid="help-search"
        />
      </div>

      {/* Section tabs (hidden when searching) */}
      {!search && (
        <div className="flex gap-1 bg-zinc-100 p-1 rounded-xl overflow-x-auto" data-testid="help-tabs">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  activeSection === s.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
                }`}
                data-testid={`help-tab-${s.id}`}
              >
                <Icon size={16} weight={activeSection === s.id ? "fill" : "regular"} style={{ color: activeSection === s.id ? s.color : undefined }} />
                {s.title}
              </button>
            );
          })}
        </div>
      )}

      {/* Content */}
      <div className="space-y-6">
        {filteredSections.map((section) => (
          <div key={section.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            {search && (
              <div className="px-5 pt-4 pb-2 flex items-center gap-2">
                <section.icon size={18} weight="fill" style={{ color: section.color }} />
                <h3 className="text-sm font-bold text-zinc-700">{section.title}</h3>
                <span className="text-xs text-zinc-400">{section.items.length} result{section.items.length !== 1 ? "s" : ""}</span>
              </div>
            )}
            <div className="px-5">
              {section.items.map((item, i) => {
                const key = `${section.id}-${i}`;
                return <FAQItem key={key} item={item} isOpen={!!openItems[key] || (search && filteredSections.length === 1 && section.items.length <= 3)} toggle={() => toggle(key)} />;
              })}
            </div>
          </div>
        ))}

        {filteredSections.length === 0 && search && (
          <div className="text-center py-12 text-zinc-400">
            <Question size={40} className="mx-auto mb-3 text-zinc-300" />
            <p className="text-sm">No results for "{search}"</p>
          </div>
        )}
      </div>

      {/* Quick cost summary card (always visible on costs tab) */}
      {activeSection === "costs" && !search && (
        <div className="bg-gradient-to-br from-[#E91E8C]/5 to-purple-50 rounded-xl border border-[#E91E8C]/20 p-5 sm:p-6">
          <h3 className="font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <Lightning size={18} weight="fill" className="text-[#E91E8C]" />
            Quick Cost Calculator
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-4 border border-zinc-100">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">5 Videos/Week</p>
              <p className="text-xl font-bold text-zinc-900">$8 - $16</p>
              <p className="text-xs text-zinc-500 mt-0.5">Fast Mode — With Lip-Sync</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-[#E91E8C]/20">
              <p className="text-xs font-bold uppercase tracking-wider text-[#E91E8C] mb-1">10 Videos/Week</p>
              <p className="text-xl font-bold text-zinc-900">$16 - $33</p>
              <p className="text-xs text-zinc-500 mt-0.5">Most popular</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-zinc-100">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">20 Videos/Week</p>
              <p className="text-xl font-bold text-zinc-900">$30 - $66</p>
              <p className="text-xs text-zinc-500 mt-0.5">Power creator</p>
            </div>
          </div>
          <p className="text-xs text-zinc-400 mt-3">* Costs are estimates. Magic Hour (Talking Photo video) is the biggest variable — pricing per their subscription plan. Fish Audio ~$0.02/video, muapi ~$0.05-0.20/video (hosting + B-roll + captions). Platform credits for text are separate.</p>
        </div>
      )}
    </div>
  );
};

export default HelpPricing;
